import { healthResponseSchema } from "@wlh/validation";
import type {
  HealthResponse,
  LearnerDashboardStatus,
  LearningItemId,
  Role,
} from "@wlh/shared-types";

const API_BASE: string = import.meta.env.VITE_API_BASE_URL ?? "/api";

export interface MeResponse {
  userId: string;
  role: Role;
  organisationId: string;
  email: string;
  displayName: string;
}

export interface MyLearningRow {
  enrollmentId: string;
  trackId: string;
  programmeTitle: string;
  trackTitle: string;
  coverImageUrl: string | null;
  state: string;
  status: LearnerDashboardStatus;
  progressPercent: number;
  nextModuleId: string | null;
  credential: LearnerTrackCredential | null;
}

export interface LearnerTrackModule {
  id: string;
  title: string;
  required: boolean;
  state: string;
  unlocked: boolean;
  learningItems: Array<{
    id: string;
    title: string;
    type: string;
    order: number;
    downloadAllowed: boolean;
    externalUrl: string | null;
  }>;
}

export interface LearnerTrackAssessment {
  maxAttempts: number;
  attemptsUsed: number;
  lastOutcome: "PASS" | "FAIL" | null;
  eligible: boolean;
}

export interface LearnerTrackCredential {
  state: "PENDING" | "ISSUED" | "ERROR";
  credentialId: string | null;
  downloadAvailable: boolean;
}

export interface LearnerTrackDiagnosticPhase {
  required: boolean;
  completed: boolean;
}
export interface LearnerTrackDiagnostic {
  pre: LearnerTrackDiagnosticPhase;
  post: LearnerTrackDiagnosticPhase;
}

export interface FeedbackQuestion {
  id: string;
  text: string;
  type: "RATING_5" | "NPS_0_10" | "TEXT" | "SINGLE_CHOICE";
  options?: string[];
}
export interface LearnerTrackFeedbackPrompt {
  templateId: string;
  questions: FeedbackQuestion[];
}

export interface LearnerTrack {
  trackTitle: string;
  programmeTitle: string;
  programmeDescription: string | null;
  coverImageUrl: string | null;
  enrollmentState: string;
  assessment: LearnerTrackAssessment | null;
  credential: LearnerTrackCredential | null;
  diagnostic: LearnerTrackDiagnostic | null;
  feedback: LearnerTrackFeedbackPrompt | null;
  modules: LearnerTrackModule[];
}

export type OpenItemResponse =
  | { kind: "EXTERNAL_LINK"; url: string | null }
  | {
      kind: "HTML_PACKAGE";
      packagesOrigin: string;
      packageUrl: string;
      sessionToken: string;
      resumeState: string | null;
      trackId: string;
    };

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: { accept: "application/json", ...(init?.headers ?? {}) },
  });
  if (!res.ok) {
    const err = new Error(`HTTP ${res.status}`) as Error & { status: number };
    err.status = res.status;
    throw err;
  }
  return (await res.json()) as T;
}

export async function getHealth(): Promise<HealthResponse> {
  return healthResponseSchema.parse(await request("/health"));
}

export async function getMe(): Promise<MeResponse> {
  return request<MeResponse>("/me");
}

export async function getMyLearning(): Promise<MyLearningRow[]> {
  return request<MyLearningRow[]>("/learning");
}

export async function getLearnerTrack(trackId: string): Promise<LearnerTrack> {
  return request<LearnerTrack>(`/learning/tracks/${trackId}`);
}

export async function openLearningItem(itemId: LearningItemId | string): Promise<OpenItemResponse> {
  return request<OpenItemResponse>(`/learning/items/${itemId}/open`, { method: "POST" });
}

/**
 * The API returns 200 for an accepted event and 422 for a validated-but-rejected
 * one (`{ accepted: false, reason }`) — both carry a real JSON body, unlike a
 * genuine network/auth failure, so this reads the body on either status instead
 * of using the generic `request()` helper (which discards the body on !ok).
 */
export async function postBridgeEvent(
  event: unknown,
): Promise<{ accepted: boolean; reason?: string }> {
  const res = await fetch(`${API_BASE}/learning/events`, {
    method: "POST",
    headers: { accept: "application/json", "content-type": "application/json" },
    body: JSON.stringify(event),
  });
  if (res.status !== 200 && res.status !== 422) {
    const err = new Error(`HTTP ${res.status}`) as Error & { status: number };
    err.status = res.status;
    throw err;
  }
  return (await res.json()) as { accepted: boolean; reason?: string };
}

export function isHttpStatus(err: unknown, status: number): boolean {
  return typeof err === "object" && err !== null && (err as { status?: number }).status === status;
}

// --- Assessment ------------------------------------------------------

export interface AssessmentQuestion {
  id: string;
  type: string;
  stem: string;
  options: Array<{ id: string; text: string }>;
}

export interface StartAssessmentResponse {
  attemptToken: string;
  attemptNumber: number;
  maxAttempts: number;
  questions: AssessmentQuestion[];
}

export interface SubmitAssessmentResponse {
  attemptNumber: number;
  maxAttempts: number;
  scorePercent: number;
  outcome: "PASS" | "FAIL";
  remediationRequired: boolean;
  responses: Array<{
    questionId: string;
    selectedOptionIds: string[];
    correct: boolean;
    correctOptionIds: string[];
    explanation: string | null;
  }>;
}

export async function startAssessment(trackId: string): Promise<StartAssessmentResponse> {
  return request<StartAssessmentResponse>(`/assessments/${trackId}/start`, { method: "POST" });
}

export async function submitAssessment(
  attemptToken: string,
  responses: Array<{ questionId: string; selectedOptionIds: string[] }>,
): Promise<SubmitAssessmentResponse> {
  return request<SubmitAssessmentResponse>(`/assessments/attempts/${attemptToken}/submit`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ responses }),
  });
}

// --- Pre/post diagnostic ----------------------------------------------

export interface StartDiagnosticResponse {
  attemptId: string;
  phase: "PRE" | "POST";
  required: boolean;
  questions: AssessmentQuestion[];
}

export interface SubmitDiagnosticResponse {
  phase: "PRE" | "POST";
  scorePercent: number;
  // Only populated for the POST phase — explanations are withheld at PRE so
  // they can't influence the "after" comparison.
  responses: Array<{
    questionId: string;
    selectedOptionIds: string[];
    correct: boolean;
    correctOptionIds: string[];
    explanation: string | null;
  }> | null;
}

export async function startDiagnostic(
  trackId: string,
  phase: "pre" | "post",
): Promise<StartDiagnosticResponse> {
  return request<StartDiagnosticResponse>(`/diagnostics/${trackId}/${phase}/start`, {
    method: "POST",
  });
}

export async function submitDiagnostic(
  attemptId: string,
  responses: Array<{ questionId: string; selectedOptionIds: string[] }>,
): Promise<SubmitDiagnosticResponse> {
  return request<SubmitDiagnosticResponse>(`/diagnostics/attempts/${attemptId}/submit`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ responses }),
  });
}

// --- Feedback ----------------------------------------------------------

export async function submitFeedback(
  trackId: string,
  templateId: string,
  answers: Array<{ questionId: string; value: string }>,
): Promise<{ submitted: boolean }> {
  return request<{ submitted: boolean }>(`/feedback/${trackId}/submit`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ templateId, answers }),
  });
}

// --- Certificate -----------------------------------------------------

export interface CertificateDownload {
  url: string;
  credentialId: string;
  issuedAt: string;
}

export async function getCertificateDownload(trackId: string): Promise<CertificateDownload> {
  return request<CertificateDownload>(`/credentials/${trackId}/download`);
}

// --- Downloadable resources --------------------------------------------

export interface ResourceDownload {
  url: string;
  filename: string;
}

export async function getResourceDownload(itemId: string): Promise<ResourceDownload> {
  return request<ResourceDownload>(`/resources/${itemId}/download`);
}
